import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { AlertCircle, ArrowLeft, Loader2 } from "lucide-react";
import { getSlicesIntradayAlgoSlicesGet } from "@/client-admin";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { buttonVariants } from "@/components/ui/button";
import { CancelPtuOrdersDialog } from "@/components/admin/CancelPtuOrdersDialog";
import { SlicesTable } from "@/components/admin/SlicesTable/SlicesTable";
import { checkSuperUserPermission, getErrorDetails } from "@/lib/utils";

type SlicesSearch = {
  delivery_date: string;
  start: string;
  end: string;
  intraday_code: string;
};

export const Route = createFileRoute("/_superuser/admin/algo-trading/slices")({
  component: SlicesPage,
  validateSearch: (search: Record<string, unknown>): SlicesSearch => ({
    delivery_date: search.delivery_date as string,
    start: search.start as string,
    end: search.end as string,
    intraday_code: search.intraday_code as string,
  }),
  beforeLoad: () => {
    checkSuperUserPermission("algo-trading");
  },
});

function SlicesPage() {
  const { delivery_date, start, end, intraday_code } = Route.useSearch();

  const {
    data: slicesData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["intraday-algo-slices", delivery_date, start, end, intraday_code],
    queryFn: () =>
      getSlicesIntradayAlgoSlicesGet({
        deliveryDate: delivery_date,
        start,
        end,
        intradayCode: intraday_code,
      }),
    refetchInterval: 10_000,
  });

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-4">
        <Link className={buttonVariants({ variant: "outline", size: "sm" })} to="/admin/algo-trading">
          <ArrowLeft className="mr-1 h-4 w-4" />
          Back
        </Link>
        <h2 className="text-lg font-semibold">
          Slices for contract: {delivery_date} {start.slice(0, 5)}&ndash;{end.slice(0, 5)}
        </h2>
        <div className="ml-auto">
          <CancelPtuOrdersDialog deliveryDate={delivery_date} start={start} end={end} />
        </div>
      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      )}

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{getErrorDetails(error)}</AlertDescription>
        </Alert>
      )}

      {slicesData && <SlicesTable rows={slicesData.data} />}
    </div>
  );
}
